import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {MatSnackBar} from '@angular/material/snack-bar';
import {FileService} from './file.service';
import {BehaviorSubject, Observable, throwError} from 'rxjs';
import {catchError, filter, finalize, map, tap} from 'rxjs/operators';
import {removeEmptyParams} from 'src/infrastructure/helper/remove-empty-params';
import {NavigationStart, Router} from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class ChunkedExportService {
  private readonly CHUNK_SIZE = 20;
  
  private isExporting = new BehaviorSubject<boolean>(false);
  public isExporting$ = this.isExporting.asObservable();
  
  private exportProgress = new BehaviorSubject<{ current: number, total: number | null }>({ 
    current: 0, 
    total: null 
  });
  public exportProgress$ = this.exportProgress.asObservable();
  
  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private fileService: FileService,
    private router: Router
  ) {
    this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe(() => {
        if (this.isExporting.value) {
          this.cancelExport();
        }
      });
  }
  
  public cancelExport(): void {
    if (this.isExporting.value) {
      this.isExporting.next(false);
      this.exportProgress.next({ current: 0, total: null });
      this.snackBar.open(
        'Export cancelled due to navigation.',
        'Dismiss',
        { duration: 3000 }
      );
    }
  }
  public exportLargeDataset(
    baseUrl: string,
    fileName: string,
    filters: Record<string, unknown>,
    totalCount?: number,
    mapFunction?: (data: unknown) => Object
  ): void {    
    this.isExporting.next(true);
    this.exportProgress.next({ current: 0, total: totalCount || null });
    
    const cleanFilters = { ...filters };
    removeEmptyParams(cleanFilters);
    
    if (!totalCount) {
      this.getRecordCount(baseUrl, cleanFilters).subscribe({
        next: (count) => {
          if (count === 0) {
            this.snackBar.open(
              `No records found matching your criteria.`,
              'Dismiss',
              { duration: 5000 }
            );
            this.isExporting.next(false);
            return;
          }
          this.startChunkedExport(baseUrl, fileName, cleanFilters, count, mapFunction);
        },
        error: (error) => {
          this.handleExportError(error);
        }
      });
    } else {
      this.startChunkedExport(baseUrl, fileName, cleanFilters, totalCount, mapFunction);
    }
  }

  private getRecordCount(baseUrl: string, filters: Record<string, unknown>): Observable<number> {
    const countParams = {
      ...filters as Record<string, string | number | boolean>,
      pageSize: 1,
      pageNumber: 1
    };
    
    return this.http.get<any>(baseUrl, { params: countParams }).pipe(      
      map((response: any) => {        
        if (response.pagination && typeof response.pagination.totalItemCount === 'number') {
          return response.pagination.totalItemCount;
        }
        
        const totalCount = response.totalCount || response.totalItems || response.total || 0;
          if (totalCount > 20000) {
          this.snackBar.open(
            "Sorry, amount of data is too large to generate file. Please use filters",
            'Dismiss',
            { duration: 8000 }
          );
          this.isExporting.next(false);
          return 0;
        }
        
        return totalCount;
      }),
      catchError((error) => {
        this.handleExportError(error);
        return throwError(() => new Error('Failed to get record count'));
      })
    );
  }

  private startChunkedExport(
    baseUrl: string,
    fileName: string,
    filters: Record<string, unknown>,
    totalCount: number,
    mapFunction?: (data: unknown) => Object
  ): void {    this.exportProgress.next({ current: 0, total: totalCount });
    
    if (totalCount > this.CHUNK_SIZE) {
      this.snackBar.open(
        "Sorry, amount of data is too large to generate file. Please use filters",
        'Dismiss',
        { duration: 8000 }
      );
      this.isExporting.next(false);
      return;
    }

    this.exportSingleChunk(baseUrl, fileName, filters, 1, 1, mapFunction);
  }
  private exportNextChunk(
    baseUrl: string,
    fileName: string,
    filters: Record<string, unknown>,
    currentChunk: number,
    totalChunks: number,
    mapFunction?: (data: unknown) => Object
  ): void {
    if (!this.isExporting.value) {
      return;
    }
    
    if (currentChunk > totalChunks) {
      this.snackBar.open(
        `Export complete! ${totalChunks} file(s) have been downloaded.`,
        'Dismiss',
        { duration: 5000 }
      );
      this.isExporting.next(false);
      return;
    }

    const chunkFileName = `${fileName}_part${currentChunk}_of_${totalChunks}`;
    
    this.exportSingleChunk(
      baseUrl,
      chunkFileName,
      filters,
      currentChunk,
      totalChunks,
      mapFunction,
      () => {
        if (this.isExporting.value) {
          this.exportNextChunk(baseUrl, fileName, filters, currentChunk + 1, totalChunks, mapFunction);
        }
      }
    );
  }

  private exportSingleChunk(
    baseUrl: string,
    fileName: string,
    filters: Record<string, unknown>,
    chunkNumber: number,
    totalChunks: number,
    mapFunction?: (data: unknown) => Object,
    onComplete?: () => void
  ): void {
    if (!this.isExporting.value) {
      return;
    }
    
    const chunkParams = {
      ...filters as Record<string, string | number | boolean>,
      pageSize: this.CHUNK_SIZE,
      pageNumber: chunkNumber
    };
    
    this.http.get<any>(baseUrl, { params: chunkParams })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 0 || error.status >= 500) {
            this.snackBar.open(
              `Server timeout or error on chunk ${chunkNumber}. Try again with a smaller dataset.`,
              'Dismiss',
              { duration: 8000 }
            );
          } else if (error.status === 401 || error.status === 403) {
            this.snackBar.open(
              `Authentication error. Please log in again.`,
              'Dismiss',
              { duration: 8000 }
            );
          }
          
          this.handleExportError(error);
          return throwError(() => new Error(`Chunk ${chunkNumber} export failed: ${error.message}`));
        }),        
        tap((response) => {
          if (!this.isExporting.value) {
            return;
          }
          
          let items = [];
          
          if (response && response.items && Array.isArray(response.items)) {
            items = response.items;
          } else if (Array.isArray(response)) {
            items = response;
          } else if (response && typeof response === 'object') {
            items = [response];
          }
          
          if (items.length === 0) {
            this.snackBar.open(
              `Warning: No data found for chunk ${chunkNumber}. This might affect export completeness.`,
              'Dismiss',
              { duration: 5000 }
            );
          }
          
          const processedItems = items.length > 0 && mapFunction 
            ? items.map(mapFunction)
            : items;
            if (processedItems.length > 0) {
              this.fileService.exportExcel(processedItems, fileName);
            
              const currentProgress = this.exportProgress.value;
              const recordsProcessed = processedItems.length;
              const newProgress = Math.min(
                ((chunkNumber - 1) * this.CHUNK_SIZE) + recordsProcessed, 
                currentProgress.total || 0
              );
            
              this.exportProgress.next({ 
                current: newProgress,
                total: currentProgress.total
              });
              this.snackBar.open(
                'File exported successfully',
                'Dismiss',
                { duration: 3000 }
              );
            
            } else {
              if (chunkNumber < totalChunks) {
                this.snackBar.open(
                  `Export may be incomplete. No more data available after chunk ${chunkNumber}.`,
                  'Dismiss',
                  { duration: 5000 }
                );
              } else {
                this.snackBar.open(
                  'File exported successfully',
                  'Dismiss',
                  { duration: 3000 }
                );
              }
          }
        }),        
        finalize(() => {
          if (!this.isExporting.value) {
            return;
          }
          
          if (onComplete) {
            const shouldContinue = this.exportProgress.value.current < (this.exportProgress.value.total || 0);
            
            if (shouldContinue) {
              const delay = Math.min(2000 + (chunkNumber * 200), 5000);
              
              setTimeout(() => {
                if (this.isExporting.value) {
                  onComplete();
                }
              }, delay);
            } else {
              this.snackBar.open(
                `Export complete! All available data has been downloaded.`,
                'Dismiss',
                { duration: 5000 }
              );
              this.isExporting.next(false);
            }
          } else {
            this.isExporting.next(false);
          }
        })
      )
      .subscribe();
  }

  private handleExportError(error: any): void {
    this.snackBar.open(
      `Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      'Dismiss',
      { duration: 7000 }
    );
    this.isExporting.next(false);
  }
}
